import React, { useState, useEffect } from 'react';
import Udesk from 'Udesk';
import Page from 'Component/common/udesk-qa-page-layout';
import { Button, Form, Select, Space, Modal } from 'udesk-ui';
import { StandardizeTable } from 'udesk_gm_ui';
import type { ColumnsType } from 'udesk-ui/lib/table';
import UdeskDatePicker from 'Component/common/udesk-date-picker';
import { hasFeature } from 'src/util/permit';
import './style.scss';
import ScoreRangeSelect from '../components/ScoreRangeSelect';
import CategoryCourseTreeSelect from 'Component/pages/components/coach/CategoryCourseTreeSelect';
import EvaluatorTreeSelect from '../components/EvaluatorTreeSelect';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import EvaluateDrawer from 'Component/common/evaluate-drawer';
import { getReviewIntelligentPartnerCourseScores } from 'src/api/reviewIntelligentPartnerCourseScores';
import { ReviewIntelligentPartnerCourseScoreFoundResponse } from 'src/api/types';
import { deleteReviewIntelligentPartnerCourseScoresById } from 'src/api/reviewIntelligentPartnerCourseScores/{id}';
import moment from 'moment';
import UdeskLocales from 'UdeskLocales';

const defaultPagination = {
    pageNum: 1,
    pageSize: 10,
    total: 0,
};

const Template = React.memo((props: any) => {
    const [form] = Form.useForm();
    const [pagination, setPagination] = React.useState(defaultPagination);
    const [loading, setLoading] = React.useState(false);
    const [dataSource, setDataSource] = React.useState<
        ReviewIntelligentPartnerCourseScoreFoundResponse[]
    >([]);
    const { ...courseTreeSelectProps } = CategoryCourseTreeSelect.useTreeSelect({ isAdmin: true });
    const { ...evaluatorTreeSelectProps } = EvaluatorTreeSelect.useTreeSelect();
    const [evaluateVisible, setEvaluateVisible] = useState(false);
    const [currentRecord, setCurrentRecord] =
        useState<ReviewIntelligentPartnerCourseScoreFoundResponse | null>(null);
    const [isView, setIsView] = useState(true);

    useEffect(() => {
        if (props.location.search) {
            const params = new URLSearchParams(props.location.search);
            const courseId = params.get('courseId');
            form.setFieldsValue({
                courseCategoryIds: {
                    courses: [courseId],
                },
            });
        }

        const formFields = form.getFieldsValue();
        handleSearch({
            ...formFields,
            ...defaultPagination,
        });
    }, []);

    const onTableRequest = async (params: any) => {
        console.log(params);
        const formFields = form.getFieldsValue();
        handleSearch({
            ...formFields,
            pageNum: params.pagination.current,
            pageSize: params.pagination.pageSize,
        });
    };

    const deleteRecord = async (id: number) => {
        setLoading(true);
        await deleteReviewIntelligentPartnerCourseScoresById({ segments: { id } });
        Udesk.ui.notify.success(/* 删除成功 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.deletedSuccessfully);
        handleSearch({
            pageNum: 1,
            pageSize: 10,
        });
    };

    const handleSearch = async (values: any) => {
        console.log(values);
        setLoading(true);
        const {
            dateRange,
            scoreRange,
            userIds,
            courseCategoryIds,
            courseCategoryId,
            textFeedbackFlag,
            pageNum,
            pageSize,
        } = values;
        const [startTime, endTime] = dateRange || [];
        const [scoreType, compareType, scoreValue] = scoreRange || [];
        const params = {
            pageNum: pageNum || defaultPagination.pageNum,
            pageSize: pageSize || defaultPagination.pageSize,
            userIds: userIds?.users?.join(','),
            courseCategoryIds: courseCategoryIds?.courses?.join(','),
            courseCategoryId: courseCategoryId,
            startTime: startTime && moment(startTime).format('YYYY-MM-DD 00:00:00'),
            endTime: endTime && moment(endTime).format('YYYY-MM-DD 23:59:59'),
            scoreNameCode: scoreType,
            symbolString: compareType,
            scoreValue: scoreValue ? Number(scoreValue) : undefined,
            textFeedbackFlag: textFeedbackFlag || '0',
        };
        try {
            const response = await getReviewIntelligentPartnerCourseScores({
                params,
            });
            setDataSource(response?.data as any);
            setPagination(response?.paging as any);
        } finally {
            setLoading(false);
        }
    };

    const resetSubmit = () => {
        form.resetFields();
        setPagination(defaultPagination);
        handleSearch({
            ...defaultPagination,
        });
    };

    const tableProps = React.useMemo(() => {
        const columns: ColumnsType<ReviewIntelligentPartnerCourseScoreFoundResponse> = [
            {
                title: /* 课程名称 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.courseName,
                dataIndex: 'courseName',
                ellipsis: true,
                width: 200,
            },
            {
                title: /* 课程类型 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.courseType,
                dataIndex: 'courseType',
                width: 120,
                render: (type) => {
                    return Udesk.enums.lessonType.getName(type) || '-';
                },
            },
            {
                title: /* 评价时间 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.evaluationTime,
                dataIndex: 'reviewTime',
                width: 160,
            },
            {
                title: /* 评价人员 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.evaluators,
                dataIndex: 'reviewerName',
                width: 120,
            },
            {
                /* 课程质量 */
                title: Udesk.enums.coachScoreTypes.courseQuality.name,
                dataIndex: Udesk.enums.coachScoreTypes.courseQuality.key,
                width: 100,
                align: 'right',
            },
            {
                /* 学习效果 */
                title: Udesk.enums.coachScoreTypes.learningEffect.name,
                dataIndex: Udesk.enums.coachScoreTypes.learningEffect.key,
                width: 100,
                align: 'right',
            },
            {
                /* 互动体验 */
                title: Udesk.enums.coachScoreTypes.interactionExperience.name,
                dataIndex: Udesk.enums.coachScoreTypes.interactionExperience.key,
                width: 100,
                align: 'right',
            },
            {
                /* 综合得分 */
                title: (
                    <div>
                        {Udesk.enums.coachScoreTypes.overallScore.name}
                        <Tooltip title={Udesk.enums.coachScoreTypes.overallScore.tooltip}>
                            <QuestionCircleOutlined style={{ marginLeft: 4, cursor: 'pointer' }} />
                        </Tooltip>
                    </div>
                ),
                dataIndex: Udesk.enums.coachScoreTypes.overallScore.key,
                width: 100,
                align: 'right',
                render: (value) => value.toFixed(2),
            },
            {
                title: /* 操作 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.operation,
                width: 180,
                fixed: 'right',
                align: 'center',
                render: (_, record) => (
                    <Space>
                        <Button
                            type="link"
                            onClick={() => {
                                setCurrentRecord(record);
                                setIsView(true);
                                setEvaluateVisible(true);
                            }}
                        >{/* 查看详情 */}{UdeskLocales['current'].pages.coach.courseEvaluation.records.index.viewDetails}</Button>
                        <Button
                            type="link"
                            onClick={() => {
                                props.history.push(
                                    `/coach/course-management/flow/${record.courseId}`
                                );
                            }}
                        >{/* 编辑课程 */}{UdeskLocales['current'].pages.coach.courseEvaluation.records.index.editCourse}</Button>
                        <Button
                            type="link"
                            danger
                            onClick={() => {
                                Modal.confirm({
                                    title: /* 确认删除 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.confirmDelete,
                                    content: /* 确定要删除该评价记录吗？ */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.areYouSureYouWantToDeleteThisEvaluationRecord,
                                    onOk: () => {
                                        // 删除操作
                                        deleteRecord(record.id!);
                                    },
                                });
                            }}
                        >{/* 删除 */}{UdeskLocales['current'].pages.coach.courseEvaluation.records.index.delete}</Button>
                    </Space>
                ),
            },
        ];

        return {
            columns,
            rowKey: 'id',
            scroll: { x: 1200 },
            dataSource,
            pagination: {
                current: pagination.pageNum,
                pageSize: pagination.pageSize,
                total: pagination.total,
            },
            loading,
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource, pagination, loading, props.history]);

    return (
        <Page
            pageBodyClassName="udesk-coach-evaluation-record"
            bodyStyle={{ background: 'transparent' }}
            title={/* 评价记录 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.evaluationRecord}
        >
            <div className="filter-form">
                <Form form={form} onFinish={handleSearch} layout="inline">
                    <Form.Item name="courseCategoryIds" label={/* 课程 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.course} style={{ width: 300 }}>
                        <CategoryCourseTreeSelect
                            {...courseTreeSelectProps}
                            placeholder={/* 请选择 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.pleaseSelect}
                            multiple
                            treeCheckable
                            maxTagCount={1}
                        />
                    </Form.Item>
                    <Form.Item name="courseCategoryId" label={/* 课程类型 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.courseType}>
                        <Select
                            placeholder={/* 请选择 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.pleaseSelect}
                            style={{ width: 200 }}
                            allowClear
                            options={Udesk.enums.lessonType
                                .filter((item) => {
                                    if (hasFeature('sys:largeAi:is:aiFlow')) {
                                        return true;
                                    }
                                    return item.key !== 'aiGen';
                                })
                                .map((i) => {
                                    return {
                                        label: i.name,
                                        value: i.id,
                                    };
                                })}
                        />
                    </Form.Item>
                    <Form.Item name="userIds" label={/* 评价人员 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.evaluators}>
                        <EvaluatorTreeSelect
                            {...evaluatorTreeSelectProps}
                            allowClear
                            placeholder={/* 请选择 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.pleaseSelect}
                            style={{ width: 200 }}
                        />
                    </Form.Item>
                    <Form.Item name="dateRange" label={/* 评价时间 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.evaluationTime}>
                        <UdeskDatePicker style={{ width: 280 }} />
                    </Form.Item>
                    <Form.Item name="scoreRange" label={/* 得分情况 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.score}>
                        <ScoreRangeSelect />
                    </Form.Item>
                    <Form.Item name="textFeedbackFlag" label={/* 文字反馈 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.textFeedback}>
                        <Select
                            placeholder={/* 全部 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.all}
                            style={{ width: 200 }}
                            options={[
                                { label: /* 全部 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.all, value: '0' },
                                { label: /* 是 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.yes, value: '2' },
                                { label: /* 否 */UdeskLocales['current'].pages.coach.courseEvaluation.records.index.no, value: '1' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button onClick={resetSubmit}>{/* 重置 */}{UdeskLocales['current'].pages.coach.courseEvaluation.records.index.reset}</Button>
                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            style={{ marginLeft: 8 }}
                        >{/* 确定 */}{UdeskLocales['current'].pages.coach.courseEvaluation.records.index.determine}</Button>
                    </Form.Item>
                </Form>
            </div>

            <StandardizeTable.RequestTable
                autoRequestOnMount={false}
                onRequest={onTableRequest}
                table={tableProps}
            />

            <EvaluateDrawer
                visible={evaluateVisible}
                onClose={() => {
                    setEvaluateVisible(false);
                    setCurrentRecord(null);
                }}
                courseName={currentRecord?.courseName}
                isView={isView}
                initialValues={{
                    courseQuality: currentRecord?.courseQuality,
                    learningEffect: currentRecord?.learningEffect,
                    interactionExperience: currentRecord?.interactionExperience,
                    feedback: currentRecord?.feedback,
                }}
            />
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
